import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as moment from "moment";
import * as momentTZ from "moment-timezone";
import { tap } from "rxjs/operators";
import { AuthService } from "../../app/auth/auth.service";
import { commonVar } from "../common-var/common";
import { ConfigurationService } from "../configuration/configuration.service";
import { UtilityService } from "../utility-service/utility-service";

const headers = new HttpHeaders().set("Content-Type", "application/json");

@Injectable()
export class DischargeHisProvider {
  private baseUrl: string;

  public activeReportId: string;
  public dischargeReportDetail;
  public patientId: string;
  public tabList = [
    {
      name: "administrationInfo",
      icon: commonVar.ADD_ICON,
      title: "A - Administrative Information",
      show: true,
    },
    {
      name: "serviceUtilization",
      icon: commonVar.ADD_ICON,
      title: "O - Service Utilization",
      show: false,
    },
    {
      name: "dischargeRecordAdministration",
      icon: commonVar.ADD_ICON,
      title: "Z - Record Administration",
      show: true,
    },
  ];

  public patientStatus: string;

  public tenantId: string;
  orgTimezone: string;

  constructor(
    public http: HttpClient,
    private config: ConfigurationService,
    private authService: AuthService,
    private utilService: UtilityService,
    private authSvc: AuthService
  ) {
    let hisAdmissionUrl = config.getConfig().hisAdmissionUrl;
    if (
      this.config.getConfig().isLocal &&
      this.config.getConfig().localEndpoints.hisAdmissionUrl
    ) {
      hisAdmissionUrl = this.config.getConfig().localEndpoints.hisAdmissionUrl;
    }
    this.baseUrl = hisAdmissionUrl + "/his/";
    const orgId = this.authSvc.getUser().tenantId;
    this.getOrgTimezone();
  }

  getActiveReportId(): string {
    return this.activeReportId;
  }

  setActiveReportId(activeReportId: string) {
    this.activeReportId = activeReportId;
  }

  getPatientId(): string {
    return this.patientId;
  }

  setPatientId(patientId: string) {
    this.patientId = patientId;
  }

  getTenantId(): string {
    return this.tenantId;
  }

  setTenantId(tenantId: string) {
    this.tenantId = tenantId;
  }

  getReportDetail() {
    return this.dischargeReportDetail;
  }

  cleanReportDetail() {
    this.dischargeReportDetail = null;
  }

  getDischargeReportList(page: number = 0) {
    const resourceUrl = `${this.baseUrl}discharge/report/patient/${this.patientId}/search?page=${page}&size=20`;
    return this.http.get(resourceUrl, { headers });
  }

  getDischargeReportListById(patientExternalID: string) {
    const resourceUrl = `${this.baseUrl}discharge/report/patient/${patientExternalID}/search`;
    return this.http.get(resourceUrl, { headers });
  }

  setDischargeReportDetail(reportId) {
    const resourceUrl = `${this.baseUrl}discharge/report/${reportId}`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http
      .get(resourceUrl, { headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((reportDetail) => {
          this.dischargeReportDetail = reportDetail;
          this.applySectionVisibility(reportDetail);
          this.setTabIcons();
        })
      );
  }

  deleteDischargeReport(reportId) {
    const resourceUrl = `${this.baseUrl}discharge/report/${reportId}`;
    return this.http.delete(resourceUrl);
  }

  initializeNullValues(reportDetail) {
    var newReportDetail = reportDetail;

    if (!newReportDetail.administrationInfo)
      newReportDetail.administrationInfo = {};
    if (!newReportDetail.serviceUtilization)
      newReportDetail.serviceUtilization = {};
    if (!newReportDetail.dischargeRecordAdministration)
      newReportDetail.dischargeRecordAdministration = {};

    return newReportDetail;
  }

  isSectionInProgress(sectionData: any): boolean {
    let result = false;

    if (!sectionData) return result;

    const objKeys = Object.keys(sectionData).filter(
      (key) => key !== "complete" && key !== "isComplete"
    );

    objKeys.forEach((key) => {
      if (sectionData[key] && sectionData[key] !== "") {
        result = true;
      }
    });

    return result;
  }

  setPrepopulatedReport(benefitToggle, billingFacilityToggle) {
    return this.http
      .get(
        `${this.baseUrl}discharge/report/patient/${this.patientId}/init?benefitToggle=${benefitToggle}&billingFacilityToggle=${billingFacilityToggle}`
      )
      // The fetch path normalises the payload; the pre-populate path did not,
      // so a new report was the one case where a section could reach the forms
      // undefined. Both paths now go through the same normalisation.
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((reportDetail) => {
          this.dischargeReportDetail = reportDetail;
          this.applySectionVisibility(reportDetail);
          this.setTabIcons();
        })
      );
  }

  /**
   * Section O ("O - Service Utilization") is only collected for a discharge
   * whose reason is EXPIRED. This used to set `show = true` and never set it
   * back, so opening an EXPIRED record and then a REVOKED one left section O
   * visible on the second — tabList is a singleton on this provider.
   */
  checkServiceUtilizationTabVisibility(reportDetail) {
    this.setServiceUtilizationVisible(this.isReasonExpired(reportDetail));
  }

  setServiceUtilizationVisible(visible: boolean) {
    this.tabList = [...this.tabList];
    this.tabList[1].show = visible;
  }

  /**
   * Apply the section-O rule to a report as it loads.
   *
   * Visibility was previously driven only by the reasonForDischarge
   * valueChanges subscription in discharge-admin-info-form. Angular does not
   * emit valueChanges for the initial `new FormControl(value)`, so re-opening a
   * saved EXPIRED discharge left section O hidden until the clinician re-picked
   * the reason. This is the load-time half of the rule.
   */
  applySectionVisibility(reportDetail) {
    this.setServiceUtilizationVisible(this.isReasonExpired(reportDetail));
  }

  isReasonExpired(reportDetail) {
    const administrationInfo = (reportDetail || {}).administrationInfo || {};
    return administrationInfo["A2115"] == "EXPIRED";
  }

  setTabIcons() {
    this.tabList.forEach((tab) => {
      const sectionData = this.dischargeReportDetail[tab.name];

      if (sectionData && tab.show) {
        if (this.isSectionInProgress(sectionData))
          tab.icon = commonVar.IN_PROGRESS_ICON;

        if (sectionData.complete) tab.icon = commonVar.COMPLETED_ICON;
      }
    });
  }

  createDischargeReport(newReport) {
    const resourceUrl = `${this.baseUrl}discharge/report/patient/${this.patientId}`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    const payload = {
      ...newReport,
      type: "discharge",
      patientId: this.patientId,
      startDate: this.getDateWithOrg(),
    };
    return this.http
      .post(resourceUrl, payload, { headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((newReport: any) => {
          this.dischargeReportDetail = newReport;
          this.activeReportId = newReport.id;
          this.setTabIcons();
        })
      );
  }

  getDateWithOrg(): string {
    let date = this.utilService.convertDateTimeWithTimezone(
      new Date(),
      this.orgTimezone
    );
    return date;
    //let datetime = this.utilService.setDateInTimeZone(new Date().toString(), this.orgTimezone, true);
    //return datetime.split('T')[0];
  }

  getOrgTimezone() {
    this.orgTimezone = this.authService.getUser().timeZoneOrg;
    // In case a valid time zone is not returned from RBAC, let's use the user/browser time zone
    if (!this.orgTimezone) {
      this.orgTimezone = momentTZ.tz.guess();
    }
  }

  updateDischargeReport(formSection, data) {
    const url = `${this.baseUrl}discharge/report/${this.activeReportId}`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");

    const update = {
      reportId: this.activeReportId,
      patientId: this.patientId,
      type: "discharge",
      endDate: this.getDateWithOrg(),
    };

    update[formSection] = data;

    return this.http
      .put(url, update, { headers: headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((reportDetail) => {
          this.dischargeReportDetail = reportDetail;
          this.setTabIcons();
        })
      );
  }

  formatDate(date) {
    return date ? moment(date).format("MM-DD-YYYY") : null;
  }

  formatlocalDatetoUTC(date) {
    if (date) {
      date = moment.parseZone(date).utc().format("YYYYMMDDTHHmmssZZ");
    }
    return date ? date : null;
  }

  formatUTCDatetoLocal(date) {
    if (date) {
      date = moment.utc(date).local().format();
    }
    return date ? date : null;
  }

  checkSectionsCompletion() {
    const detail = this.dischargeReportDetail || {};
    // A section the API omitted is by definition not complete — reading
    // `.complete` off it used to throw.
    return !!(detail.administrationInfo && detail.administrationInfo.complete);
    // Section O completion is deliberately not required here; see CO-3369.
  }
}



// WEBPACK FOOTER //
// ./src/providers/discharge-his/discharge-his.ts